import type { Vehicle, VehicleListResponse } from '../services/vehicle.service'

export const VEHICLE_TRANSFER_SCHEMA = 'tms.vehicles.v1'
export const MAX_IMPORT_ROWS = 1000
const numericFields = ['year', 'capacity_volume', 'capacity_weight', 'empty_weight', 'length', 'width', 'height', 'tail_lift_capacity', 'max_weight_permitted', 'max_combination_weight', 'total_mileage', 'fuel_consumption_average', 'maintenance_cost_per_km', 'breakdown_rate', 'availability_rate', 'average_rating', 'max_driving_distance_per_day', 'hourly_cost', 'cost_per_km', 'daily_fixed_cost', 'max_floor_pressure', 'pressure_cell_target_size', 'target_cg_x', 'target_cg_y', 'max_cg_deviation', 'favorite_rating']
const booleanFields = ['has_reefer_unit', 'is_hazardous_compatible', 'has_tail_lift', 'has_loading_ramp', 'is_overweight_permitted', 'requires_special_license', 'has_gps_tracking', 'has_tachograph', 'has_video_surveillance']
const arrayFields = ['hazardous_certifications', 'preferred_regions', 'restricted_regions', 'special_equipment', 'obstacles']
const objectFields = ['reefer_temperature_range', 'mass_provenance', 'axle_configuration']
const stringFields = ['registration_number', 'type', 'vehicle_subtype', 'container_type', 'brand', 'model', 'fuel_type', 'euro_emission_standard', 'mass_contract_version', 'required_license_type', 'status']
export const VEHICLE_TRANSFER_FIELDS = [...stringFields, ...numericFields, ...booleanFields, ...arrayFields, ...objectFields]
const subtypes = ['porteur_2_essieux_12t', 'porteur_2_essieux_18t', 'porteur_3_essieux_26t', 'tracteur_4x2', 'tracteur_6x2', 'semi_remorque_2_essieux_34t', 'semi_remorque_3_essieux_44t', 'remorque_2_essieux', 'remorque_3_essieux', 'fourgonnette_standard']
const containers = ['dry_20', 'dry_40', 'dry_45', 'high_cube_40', 'high_cube_45', 'reefer_20', 'reefer_40', 'open_top_20', 'open_top_40', 'flat_rack_20', 'flat_rack_40', 'tank_20', 'tank_40']
const enums: Record<string, string[]> = {
  type: ['TRUCK', 'RIGID_TRUCK', 'VAN', 'TRAILER', 'SEMI_TRAILER', 'CONTAINER', 'OTHER'],
  status: ['active', 'inactive', 'maintenance', 'out_of_service'],
  vehicle_subtype: [...subtypes, ...containers], container_type: containers,
  fuel_type: ['diesel', 'gasoline', 'electric', 'hybrid', 'lpg', 'cng'],
  euro_emission_standard: ['EURO_1', 'EURO_2', 'EURO_3', 'EURO_4', 'EURO_5', 'EURO_6', 'EURO_7'],
}

export type VehicleImportRow = { row: number; vehicle: Partial<Vehicle>; errors: string[]; duplicate: boolean }
export const registrationKey = (value: string) => value.trim().toLocaleUpperCase('fr-FR')

// An explicit portable contract excludes database IDs, tenant data, relations and UI state.
export function portableVehicle(vehicle: Partial<Vehicle>): Partial<Vehicle> {
  return Object.fromEntries(VEHICLE_TRANSFER_FIELDS.filter(key => Object.prototype.hasOwnProperty.call(vehicle, key)).map(key => [key, vehicle[key as keyof Vehicle]]))
}

function parseCsv(text: string): Record<string, unknown>[] {
  const headerLine = text.split(/\r?\n/, 1)[0]
  const separator = headerLine.includes(';') ? ';' : headerLine.includes('\t') ? '\t' : ','
  const rows: string[][] = []
  let row: string[] = [], cell = '', quoted = false, closed = false
  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') { cell += '"'; i++ }
      else if (char === '"') { quoted = false; closed = true }
      else cell += char
    } else if (char === '"' && !cell && !closed) quoted = true
    else if (char === separator || char === '\n' || char === '\r') {
      row.push(cell); cell = ''; closed = false
      if (char !== separator) {
        if (char === '\r' && text[i + 1] === '\n') i++
        if (row.some(value => value.trim())) rows.push(row)
        row = []
      }
    } else {
      if (closed || char === '"') throw new Error('CSV invalide : guillemets mal placés.')
      cell += char
    }
  }
  if (quoted) throw new Error('CSV invalide : guillemet non fermé.')
  row.push(cell)
  if (row.some(value => value.trim())) rows.push(row)
  const headers = rows.shift()?.map(value => value.trim()) ?? []
  if (!headers.includes('type')) throw new Error('Colonne obligatoire : type. Téléchargez le fichier exemple.')
  if (new Set(headers).size !== headers.length || headers.some(key => !key)) throw new Error('Les en-têtes CSV doivent être uniques et non vides.')
  return rows.map((values, index) => {
    if (values.length !== headers.length) throw new Error(`Ligne ${index + 2} : nombre de colonnes incorrect.`)
    return Object.fromEntries(headers.map((key, i) => [key, values[i] === '\\N' ? null : values[i].replace(/^'(?=[=+\-@\t\r]|\\N$)/, '')]))
  })
}

export function parseVehicleFile(text: string, format: 'json' | 'csv'): unknown[] {
  const clean = text.replace(/^\uFEFF/, '')
  let rows: unknown
  if (format === 'csv') rows = parseCsv(clean)
  else {
    let parsed: unknown
    try { parsed = JSON.parse(clean) } catch { throw new Error('Le fichier JSON est invalide.') }
    if (Array.isArray(parsed)) rows = parsed
    else if (parsed && typeof parsed === 'object' && 'schema' in parsed && 'vehicles' in parsed) {
      if (parsed.schema !== VEHICLE_TRANSFER_SCHEMA) throw new Error('Version du fichier non prise en charge.')
      const units = (parsed as { units?: Record<string, string> }).units
      if (units?.length !== 'mm' || units?.mass !== 'kg' || units?.volume !== 'm3') throw new Error('Unités attendues : mm, kg et m3.')
      rows = parsed.vehicles
    }
  }
  if (!Array.isArray(rows) || !rows.length) throw new Error('Le fichier doit contenir au moins un véhicule.')
  if (rows.length > MAX_IMPORT_ROWS) throw new Error(`Limite de ${MAX_IMPORT_ROWS} véhicules par import.`)
  return rows
}

export function validateVehicleRows(rows: unknown[], existing: Partial<Vehicle>[], registrationRequired = false): VehicleImportRow[] {
  const seen = new Set(existing.map(vehicle => registrationKey(vehicle.registration_number || '')))
  return rows.map((raw, index) => {
    const errors: string[] = []
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { row: index + 1, vehicle: {}, errors: ['Objet véhicule attendu.'], duplicate: false }
    const vehicle = portableVehicle(raw as Partial<Vehicle>) as Record<string, unknown>
    for (const key of VEHICLE_TRANSFER_FIELDS) {
      let value = vehicle[key]
      if (value === undefined) continue
      if (value === '' || (typeof value === 'string' && !value.trim())) { delete vehicle[key]; continue }
      if (value === null) {
        if (['registration_number', 'type', 'status', 'fuel_type', 'euro_emission_standard'].includes(key)) delete vehicle[key]
        continue
      }
      if (numericFields.includes(key)) {
        value = typeof value === 'string' ? Number(value.trim().replace(',', '.')) : value
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) errors.push(`${key} : nombre positif ou nul attendu.`)
        else if (['length', 'width', 'height'].includes(key) && value === 0) errors.push(`${key} doit être supérieur à zéro.`)
        else if (['year', 'favorite_rating', 'pressure_cell_target_size'].includes(key) && !Number.isInteger(value)) errors.push(`${key} : entier attendu.`)
        else if (['target_cg_x', 'target_cg_y', 'max_cg_deviation'].includes(key) && value > 1) errors.push(`${key} doit être compris entre 0 et 1.`)
        else if (['average_rating', 'favorite_rating'].includes(key) && value > 5) errors.push(`${key} : maximum 5.`)
        else if (['availability_rate', 'breakdown_rate'].includes(key) && value > 100) errors.push(`${key} : maximum 100.`)
        else if (key === 'pressure_cell_target_size' && (value < 100 || value > 500)) errors.push('pressure_cell_target_size : de 100 à 500 mm.')
      } else if (booleanFields.includes(key)) {
        if (typeof value === 'string' && ['true', 'false', '1', '0'].includes(value.toLowerCase())) value = ['true', '1'].includes(value.toLowerCase())
        if (typeof value !== 'boolean') errors.push(`${key} : true ou false attendu.`)
      } else if (arrayFields.includes(key) || objectFields.includes(key)) {
        if (typeof value === 'string') { try { value = JSON.parse(value) } catch { errors.push(`${key} : JSON invalide.`) } }
        if (arrayFields.includes(key) ? !Array.isArray(value) : !value || typeof value !== 'object' || Array.isArray(value)) errors.push(`${key} : structure invalide.`)
        if (arrayFields.includes(key) && key !== 'obstacles' && Array.isArray(value) && value.some(item => typeof item !== 'string')) errors.push(`${key} : liste de textes attendue.`)
      } else if (typeof value !== 'string') {
        errors.push(`${key} : texte attendu.`)
        delete vehicle[key]
        continue
      }
      else {
        value = value.trim()
        if (enums[key] && !enums[key].includes(value as string)) {
          errors.push(`${key} : valeur non reconnue.`)
          delete vehicle[key]
          continue
        }
        const maxLength = key === 'mass_contract_version' ? 64 : 50
        if (['registration_number', 'brand', 'model', 'mass_contract_version'].includes(key) && (value as string).length > maxLength) errors.push(`${key} : maximum ${maxLength} caractères.`)
      }
      vehicle[key] = value
    }
    if (registrationRequired && !vehicle.registration_number) errors.push('Immatriculation ou référence obligatoire.')
    vehicle.registration_number ??= null
    if (!vehicle.type) errors.push('Type de véhicule obligatoire.')
    validateTechnicalConfiguration(vehicle, errors)
    vehicle.status ??= 'active'
    const key = registrationKey(String(vehicle.registration_number || ''))
    const duplicate = !!key && seen.has(key)
    // An invalid row must not reserve the registration of a later valid row.
    if (key && !errors.length) seen.add(key)
    return { row: index + 1, vehicle: vehicle as Partial<Vehicle>, errors, duplicate }
  })
}

function validateTechnicalConfiguration(vehicle: Record<string, unknown>, errors: string[]) {
  const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value)
  const positive = (value: unknown) => typeof value === 'number' && Number.isFinite(value) && value >= 0
  const config = vehicle.axle_configuration
  if (object(config)) {
    const physicalTypes = ['rigid_truck', 'truck', 'tractor_semitrailer', 'van', 'trailer', 'container', 'custom']
    if (config.physicsType !== undefined && !physicalTypes.includes(String(config.physicsType))) errors.push('axle_configuration.physicsType : valeur non reconnue.')
    for (const key of ['trackWidth', 'kingPinPosition', 'fifthWheelMaxLoad', 'influenceRadius', 'fifthWheelHeight', 'kingPinHeight']) {
      if (config[key] != null && !positive(config[key])) errors.push(`axle_configuration.${key} : nombre positif ou nul attendu.`)
    }
    if (config.enforceAxleLimits != null && typeof config.enforceAxleLimits !== 'boolean') errors.push('axle_configuration.enforceAxleLimits : booléen attendu.')
    if (config.axles !== undefined) {
      if (!Array.isArray(config.axles) || config.axles.some(axle => !object(axle) || typeof axle.id !== 'string' || !axle.id || !positive(axle.position) || !positive(axle.maxWeight) || (axle.emptyWeight != null && !positive(axle.emptyWeight)))) errors.push('Configuration des essieux invalide : id, position et maxWeight sont requis (mm, kg).')
      else if (new Set(config.axles.map(axle => axle.id)).size !== config.axles.length) errors.push('Les identifiants des essieux doivent être uniques.')
    }
    if (config.axleGroups !== undefined && (!Array.isArray(config.axleGroups) || config.axleGroups.some(group => !object(group) || typeof group.id !== 'string' || !['single', 'tandem', 'tridem', 'custom'].includes(String(group.type))))) errors.push('Groupes d’essieux invalides.')
  }
  if (Array.isArray(vehicle.obstacles) && vehicle.obstacles.some(obstacle => {
    if (!object(obstacle) || typeof obstacle.id !== 'string' || typeof obstacle.name !== 'string') return true
    const { position, dimensions } = obstacle
    return !object(position) || !['x', 'y', 'z'].every(key => positive(position[key])) || !object(dimensions) || !['length', 'width', 'height'].every(key => positive(dimensions[key]) && Number(dimensions[key]) > 0)
  })) errors.push('Obstacles invalides : identité, position et dimensions en mm requises.')
  const temperature = vehicle.reefer_temperature_range
  if (object(temperature) && (typeof temperature.min !== 'number' || !Number.isFinite(temperature.min) || typeof temperature.max !== 'number' || !Number.isFinite(temperature.max) || temperature.min > temperature.max || (temperature.unit !== undefined && !['C', 'F', 'K'].includes(String(temperature.unit))))) errors.push('Plage de température invalide : min doit être inférieur ou égal à max.')
}

export function serializeVehicles(vehicles: Partial<Vehicle>[], format: 'json' | 'csv'): string {
  const rows = vehicles.map(portableVehicle)
  if (format === 'json') return JSON.stringify({ schema: VEHICLE_TRANSFER_SCHEMA, exported_at: new Date().toISOString(), units: { length: 'mm', mass: 'kg', volume: 'm3' }, vehicles: rows }, null, 2)
  const escape = (value: unknown) => {
    let text = value === null ? '\\N' : value === undefined ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value)
    if (/^[=+\-@\t\r]/.test(text) || (value !== null && text === '\\N')) text = `'${text}`
    return `"${text.replace(/"/g, '""')}"`
  }
  return '\uFEFF' + [VEHICLE_TRANSFER_FIELDS.map(escape).join(';'), ...rows.map(row => VEHICLE_TRANSFER_FIELDS.map(key => escape(row[key as keyof Vehicle])).join(';'))].join('\r\n')
}

export function downloadVehicleFile(content: string, filename: string, format: 'json' | 'csv') {
  const url = URL.createObjectURL(new Blob([content], { type: format === 'json' ? 'application/json;charset=utf-8' : 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url; link.download = filename
  document.body.appendChild(link); link.click(); link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export async function collectAllVehicles(fetchPage: (params: { page: number; pageSize: number }) => Promise<VehicleListResponse>): Promise<Vehicle[]> {
  const vehicles: Vehicle[] = []
  const ids = new Set<string>()
  let total = -1
  for (let page = 1; page <= 10000; page++) {
    const result = await fetchPage({ page, pageSize: 100 })
    const pagination = result.pagination
    if (!pagination || !Number.isInteger(pagination.totalPages) || !Number.isInteger(pagination.total) || pagination.total < 0 || pagination.totalPages < 0 || (total !== -1 && total !== pagination.total)) throw new Error('La flotte a changé ou sa pagination est invalide. Veuillez réessayer.')
    total = pagination.total
    for (const vehicle of result.data) {
      if (!vehicle.id || ids.has(vehicle.id)) throw new Error('La flotte a changé pendant la lecture. Veuillez réessayer.')
      ids.add(vehicle.id); vehicles.push(vehicle)
    }
    if (page >= pagination.totalPages) {
      if (vehicles.length !== total) throw new Error('La lecture de la flotte est incomplète. Veuillez réessayer.')
      return vehicles
    }
    if (!result.data.length) throw new Error('La lecture de la flotte est incomplète. Veuillez réessayer.')
  }
  throw new Error('La flotte dépasse la limite de lecture.')
}
